/**
 * DriftWatchOverview — Owner-only operational dashboard for every drift watch
 * across all tenants. Surfaces last-alert times, alert counts, threshold
 * settings, and the most recent observed delta so the Programme team can
 * see "where ERIO proactively flagged risk this week" at a glance — and
 * disable noisy watches from one place.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, AlertTriangle, CheckCircle2, Clock, Trash2 } from "lucide-react";

import { Navbar } from "@/components/Navbar";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useCollapsible } from "@/components/CollapsibleList";


function fmtAgo(iso) {
  if (!iso) return "—";
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  if (mins < 24 * 60) return `${Math.round(mins / 60)}h ago`;
  return `${Math.round(mins / (24 * 60))}d ago`;
}


export default function DriftWatchOverview() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/drift-watches/overview");
      setItems(data.items || []);
      setScope(data.scope || "");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load watches");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (w) => {
    try {
      await api.put(`/programmes/${w.programme_id}/drift-watches/${w.connector_id}`,
                    { enabled: !w.enabled });
      toast.success(w.enabled ? "Watch disabled" : "Watch enabled");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not toggle");
    }
  };

  const remove = async (w) => {
    if (!window.confirm(`Delete the drift watch for "${w.programme_name}" against ${w.connector_name}?`)) return;
    try {
      await api.delete(`/programmes/${w.programme_id}/drift-watches/${w.connector_id}`);
      toast.success("Watch removed");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not remove");
    }
  };

  const stats = {
    total: items.length,
    enabled: items.filter((i) => i.enabled).length,
    alerted24h: items.filter((i) => i.last_alert_at &&
      (Date.now() - new Date(i.last_alert_at).getTime()) < 24 * 60 * 60 * 1000).length,
    erroring: items.filter((i) => i.last_check_error).length,
  };

  const { visible, Toggle } = useCollapsible(items, 10, "drift-watch-overview");

  return (
    <div className="min-h-screen bg-ek-canvas" data-testid="drift-watch-overview-page">
      <Navbar />
      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <header className="mb-6">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-ek-purple">Owner operational view</p>
          <h1 className="mt-1 text-3xl font-semibold text-ek-ink">Drift Watches</h1>
          <p className="mt-1 max-w-3xl text-sm text-ek-ink/65">
            Every (programme × connector) pair being ambiently monitored.
            ERIO checks each watch hourly and emails the CRO with the drift PDF when thresholds breach (24h cooldown).
            {scope === "all_tenants" && " You're viewing watches across all tenants."}
          </p>
        </header>

        <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatCard label="Watches configured" value={stats.total} testid="dwo-stat-total" />
          <StatCard label="Currently enabled" value={stats.enabled} testid="dwo-stat-enabled"
                    accent={stats.enabled > 0 ? "emerald" : null} />
          <StatCard label="Alerts in last 24h" value={stats.alerted24h} testid="dwo-stat-alerted24h"
                    accent={stats.alerted24h > 0 ? "amber" : null} />
          <StatCard label="Watches with errors" value={stats.erroring} testid="dwo-stat-erroring"
                    accent={stats.erroring > 0 ? "red" : null} />
        </div>

        {loading ? (
          <p className="text-sm text-ek-ink/65">Loading watches…</p>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-black/5 bg-white p-8 text-center" data-testid="dwo-empty">
            <Bell size={28} className="mx-auto mb-3 text-ek-ink/30" />
            <p className="font-medium text-ek-ink">No drift watches yet.</p>
            <p className="mt-1 text-sm text-ek-ink/60">
              Open any <Link to="/programmes" className="text-violet-700 hover:underline">programme</Link>,
              run a drift comparison, and toggle "Watch this drift" to get ambient alerts when thresholds breach.
            </p>
          </div>
        ) : (
          <div className="rounded-2xl border border-black/5 bg-white" data-testid="dwo-table-wrap">
            <table className="w-full text-left text-sm">
              <thead className="text-xs uppercase tracking-wide text-ek-ink/60">
                <tr className="border-b border-black/5">
                  <th className="py-3 pl-5 pr-3 font-semibold">Programme · Connector</th>
                  {scope === "all_tenants" && <th className="py-3 pr-3 font-semibold">Tenant</th>}
                  <th className="py-3 pr-3 font-semibold">Status</th>
                  <th className="py-3 pr-3 font-semibold">Thresholds</th>
                  <th className="py-3 pr-3 font-semibold">Last alert</th>
                  <th className="py-3 pr-3 font-semibold">Alert count</th>
                  <th className="py-3 pr-5 text-right font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((w) => (
                  <tr key={w.watch_id} data-testid={`dwo-row-${w.watch_id}`}
                      className="border-b border-black/5 last:border-b-0 transition-colors hover:bg-ek-purple/[0.02]">
                    <td className="py-3 pl-5 pr-3">
                      <Link to={`/programmes/${w.programme_id}`}
                            className="font-medium text-ek-ink hover:text-ek-purple">
                        {w.programme_name}
                      </Link>
                      <p className="text-[11px] text-ek-ink/55">vs {w.connector_name} ({w.connector_slug || "?"})</p>
                    </td>
                    {scope === "all_tenants" && (
                      <td className="py-3 pr-3 text-xs text-ek-ink/65">{w.tenant_name}</td>
                    )}
                    <td className="py-3 pr-3">
                      {w.last_check_error ? (
                        <span title={w.last_check_error}
                              className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-[10.5px] font-semibold text-red-700 ring-1 ring-red-200">
                          <AlertTriangle size={10} /> Errored
                        </span>
                      ) : w.enabled ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10.5px] font-semibold text-emerald-700 ring-1 ring-emerald-200">
                          <CheckCircle2 size={10} /> Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10.5px] font-semibold text-slate-600 ring-1 ring-slate-200">
                          Paused
                        </span>
                      )}
                    </td>
                    <td className="py-3 pr-3 text-xs text-ek-ink/70">
                      <p>Δ ≤ <span className="font-mono">{w.threshold_delta}</span></p>
                      <p>or mismatches ≥ <span className="font-mono">{w.threshold_mismatches}</span></p>
                    </td>
                    <td className="py-3 pr-3 text-xs">
                      {w.last_alert_at ? (
                        <>
                          <p className="font-medium text-amber-800">{fmtAgo(w.last_alert_at)}</p>
                          {w.last_alert_delta !== null && w.last_alert_delta !== undefined && (
                            <p className="text-ek-ink/55">Δ {w.last_alert_delta.toFixed(3)}</p>
                          )}
                        </>
                      ) : (
                        <p className="text-ek-ink/40 inline-flex items-center gap-1">
                          <Clock size={11} /> Never
                        </p>
                      )}
                    </td>
                    <td className="py-3 pr-3 text-xs font-mono text-ek-ink/65">{w.alert_count}</td>
                    <td className="py-3 pl-3 pr-5 text-right">
                      <button onClick={() => toggle(w)}
                              data-testid={`dwo-toggle-${w.watch_id}`}
                              className="rounded-md border border-black/10 bg-white px-2 py-1 text-[11px] font-medium text-ek-ink hover:bg-ek-purple/5">
                        {w.enabled ? "Pause" : "Enable"}
                      </button>
                      <button onClick={() => remove(w)}
                              data-testid={`dwo-remove-${w.watch_id}`}
                              className="ml-1 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-[11px] font-medium text-red-700 hover:bg-red-100">
                        <Trash2 size={11} />
                      </button>
                    </td>
                  </tr>
                ))}
                <Toggle colSpan={scope === "all_tenants" ? 7 : 6} />
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}


function StatCard({ label, value, testid, accent }) {
  const accentClass = accent === "emerald" ? "border-emerald-200 bg-emerald-50/40"
    : accent === "amber" ? "border-amber-200 bg-amber-50/40"
    : accent === "red" ? "border-red-200 bg-red-50/40"
    : "border-black/5 bg-white";
  return (
    <div data-testid={testid}
         className={`rounded-2xl border p-4 ${accentClass}`}>
      <p className="text-xs font-medium text-ek-ink/65">{label}</p>
      <p className="mt-1 text-3xl font-semibold tabular-nums text-ek-ink">{value}</p>
    </div>
  );
}
